import type { ReactNode } from 'react';
import { clsx, type ClassValue } from 'clsx';
import { extendTailwindMerge } from 'tailwind-merge';

import { THEME_UTILITY_NAMES as N } from './themeUtilityNames';

/**
 * tailwind-merge taught the WEB-1000 `@theme` names, so `text-small` merges as a
 * font size, `rounded-pill` / `z-modal` / `duration-micro` as their groups, and
 * the `loci-*` shadows as shadows (not shadow colours). See `themeUtilityNames.ts`.
 */
const twMerge = extendTailwindMerge({
  extend: {
    theme: {
      color: [...N.color],
      font: [...N.font],
      text: [...N.text],
      radius: [...N.radius],
      spacing: [...N.spacing],
      container: [...N.container],
      shadow: [...N.shadow],
      ease: [...N.ease],
      tracking: [...N.tracking],
      leading: [...N.leading],
      blur: [...N.blur],
    },
    classGroups: {
      z: [{ z: [...N.zIndex] }],
      duration: [{ duration: [...N.duration] }],
      'bg-image': [{ bg: [...N.backgroundImage] }],
    },
  },
});

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * `true` when a `ReactNode` renders visible content — i.e. it is not one of the
 * values React renders as nothing (`undefined`, `null`, `''`, or a boolean).
 * Note `0` is renderable (React prints "0"). Used by informational chip atoms
 * (`LocusCite`, `LocusCoordStrip`) to guard an always-flex slot so an empty
 * value doesn't leave a phantom `gap`.
 */
export function isRenderableNode(node: ReactNode): boolean {
  return node !== undefined && node !== null && node !== '' && typeof node !== 'boolean';
}
